import { CenteredLayout, ContentHeader, Css, useComputed } from "@homebound/beam";
import { FieldState, ObjectConfig, useFormState } from "@homebound/form-state";
import { BoundAiSelectField } from "src/components/formFields/ai";
import {
  aiFieldProposalConfig,
  AiFieldProposalFormValue,
} from "src/components/formFields/ai/utils/aiFieldProposalForm";
import {
  flattenEntityProposals,
  hasPendingProposals,
  toResolveInputs,
} from "src/components/formFields/ai/utils/aiProposalForm";
import { parseAiNumber, parseAiString } from "src/components/formFields/ai/utils/bindAiProposal";
import {
  AiFieldBinding,
  aiFieldBinding,
  reconcilePendingAiProposals,
} from "src/components/formFields/ai/utils/reconcilePendingAiProposals";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType, IncrementalCollectionOp, Maybe, SaveProgramDataInput } from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { PlanPackageAiReviewBanner } from "src/routes/libraries/plan-package-v2/components/PlanPackageAiReviewBanner";
import {
  PlanPackageProgramDataV2_PlanPackageFragment,
  PlanPackageProgramDataV2_ProgramDataFragment,
  PlanPackageProgramDataV2Query,
  usePlanPackageProgramDataV2Query,
  useSavePlanPackageProgramDataV2Mutation,
} from "src/routes/libraries/plan-package-v2/components/ProgramDataTab.generated";
import {
  ProgramDataCard,
  ProgramDataFeature,
} from "src/routes/libraries/plan-package-v2/components/program-data/ProgramDataCard";
import { queryResult } from "src/utils";

type ProgramDataTabProps = { planPackageId: string; versionId: string };

export function ProgramDataTab(props: ProgramDataTabProps) {
  const { planPackageId, versionId } = props;
  const query = usePlanPackageProgramDataV2Query({ variables: { id: planPackageId, versionId } });
  return queryResult(query, ({ planPackage, enumDetails }) => (
    <DataView planPackage={planPackage} enumDetails={enumDetails} />
  ));
}

type DataViewProps = {
  enumDetails: PlanPackageProgramDataV2Query["enumDetails"];
  planPackage: PlanPackageProgramDataV2_PlanPackageFragment;
};

function DataView(props: DataViewProps) {
  const { planPackage, enumDetails } = props;
  const { programData, minProgramData: min, maxProgramData: max } = planPackage;
  const navigateToRpav = useNavigateToRpav();
  const [save] = useSavePlanPackageProgramDataV2Mutation();
  const aiEnabled = useFeatureFlag(FeatureFlagType.PlanPackagesAi);

  const formState = useFormState({
    config: programDataConfig,
    init: { input: planPackage, map: (pp) => mapToForm(pp, aiEnabled) },
    autoSave: (state) => saveProgramData(state.changedValue),
  });

  async function saveProgramData(changedValue: Partial<ProgramDataForm>) {
    const { fieldProposals: changedProposals, ...programDataChanges } = changedValue;
    const proposals = toResolveInputs(changedProposals);
    const hasProgramData = Object.keys(programDataChanges).length > 0;
    if (!hasProgramData && proposals.length === 0) return;

    const { data } = await save({
      variables: {
        input: {
          id: planPackage.id,
          ...(hasProgramData
            ? {
                programDatas: [
                  {
                    id: programData.readyPlanProgramData?.id,
                    op: IncrementalCollectionOp.Include,
                    programData: programDataChanges,
                  },
                ],
              }
            : {}),
          ...(proposals.length > 0 ? { proposals } : {}),
        },
      },
    });
    const saved = data?.savePlanPackage.planPackage;
    if (saved && saved.version.id !== planPackage.version.id) {
      // Saving a published version bumped us to a new draft, so clear dirty fields before navigating to it.
      formState.update(saved);
      navigateToRpav(saved.version.id);
    }
  }

  const hasPending = useComputed(() => hasPendingProposals(formState.fieldProposals.rows), [formState]);

  const disabled = disableBasedOnPotentialOperation(planPackage.canEdit);
  const aiFields: AiFieldBinding[] = [];
  const feature = (label: string, field: keyof ProgramDataNumerics): ProgramDataFeature => {
    const aiProposal = formState.fieldProposals.rows.find((p) => p.fieldName.value === field);
    const binding = aiFieldBinding(formState[field], parseAiNumber, aiProposal);
    aiFields.push(binding);
    return { label, base: formState[field], min: min[field], max: max[field], aiProposal, binding };
  };
  const enumProposal = (field: keyof ProgramDataEnums) => {
    const aiProposal = formState.fieldProposals.rows.find((p) => p.fieldName.value === field);
    // Enum fields hold their `.code`, so the proposal's raw string is the value to set.
    aiFields.push(aiFieldBinding(formState[field] as FieldState<Maybe<string>>, parseAiString, aiProposal));
    return aiProposal;
  };

  return (
    <div css={Css.df.fdc.gap3.$}>
      <PlanPackageAiReviewBanner
        hasPending={hasPending}
        // Program Data has no derived-warning concept the way Locations does.
        warnings={0}
        onReconcileAll={(decision) => reconcilePendingAiProposals(aiFields, decision)}
      />
      <CenteredLayout size="lg">
        <ContentHeader
          title="Program Data"
          description="Enter base program data and the system will calculate the min and max values that can be configured with options."
          xss={Css.pb3.$}
          withAutoSave
        />

        {/* Two equal columns that stack into one when the container can't fit both */}
        <div css={Css.dg.gtc("repeat(auto-fit, minmax(min(400px, 100%), 1fr))").aifs.gap4.$}>
          <div css={Css.df.fdc.gap3.$}>
            <ProgramDataCard
              title="General"
              icon="house"
              features={[feature("Stories", "stories")]}
              disabled={disabled}
            />
            <ProgramDataCard
              title="Bedrooms"
              icon="bed"
              enumField={
                <BoundAiSelectField
                  fullWidth
                  label="Primary Bedroom Location"
                  field={formState.primaryBedroom}
                  options={enumDetails.programDataPrimaryBedroomConfig.map(({ code, name }) => ({ id: code, name }))}
                  aiProposal={enumProposal("primaryBedroom")}
                  disabled={disabled}
                />
              }
              features={[
                feature("Bedrooms", "bedrooms"),
                feature("Closets in Primary Suite", "closetsInPrimarySuite"),
                feature("First Floor Bedrooms", "firstFloorBedrooms"),
              ]}
              disabled={disabled}
            />
            <ProgramDataCard
              title="Bathroom"
              icon="bath"
              features={[feature("Full Baths", "fullBaths"), feature("Half Baths", "halfBaths")]}
              disabled={disabled}
            />
            <ProgramDataCard
              title="Dining"
              icon="dinner"
              features={[feature("Formal", "diningRoom"), feature("Casual", "casualDining")]}
              disabled={disabled}
            />
          </div>
          <div css={Css.df.fdc.gap3.$}>
            <ProgramDataCard
              title="Entertainment"
              icon="television"
              features={[feature("Media Rooms", "mediaRooms"), feature("Lofts / Game / Flex", "loftGameFlexRooms")]}
              disabled={disabled}
            />
            <ProgramDataCard
              title="Work From Home"
              icon="monitor"
              features={[feature("Offices", "offices"), feature("Workspaces", "workspaces")]}
              disabled={disabled}
            />
            <ProgramDataCard
              title="Garage"
              icon="car"
              enumField={
                <BoundAiSelectField
                  label="Garage Load"
                  fullWidth
                  field={formState.garageConfiguration}
                  options={enumDetails.programDataGarageConfig.map(({ code, name }) => ({ id: code, name }))}
                  aiProposal={enumProposal("garageConfiguration")}
                  disabled={disabled}
                />
              }
              features={[
                feature("Attached", "garageAttached"),
                feature("Detached", "garageDetached"),
                feature("Carport", "garagePort"),
              ]}
              disabled={disabled}
            />
            <ProgramDataCard
              title="Basement"
              icon="arrowFromTop"
              enumField={
                <BoundAiSelectField
                  label="Basement Type"
                  fullWidth
                  field={formState.basementConfig}
                  options={enumDetails.programDataBasementConfig.map(({ code, name }) => ({ id: code, name }))}
                  aiProposal={enumProposal("basementConfig")}
                  disabled={disabled}
                />
              }
              disabled={disabled}
            />
          </div>
        </div>
      </CenteredLayout>
    </div>
  );
}

/** The fields that have a computed min/max, i.e. every numeric field. */
type ProgramDataNumerics = Omit<PlanPackageProgramDataV2_ProgramDataFragment, "__typename" | "id">;

type ProgramDataEnums = Pick<SaveProgramDataInput, "primaryBedroom" | "garageConfiguration" | "basementConfig">;

type ProgramDataForm = ProgramDataNumerics & ProgramDataEnums & { fieldProposals: AiFieldProposalFormValue[] };

const programDataConfig: ObjectConfig<ProgramDataForm> = {
  stories: { type: "value" },
  bedrooms: { type: "value" },
  closetsInPrimarySuite: { type: "value" },
  firstFloorBedrooms: { type: "value" },
  fullBaths: { type: "value" },
  halfBaths: { type: "value" },
  diningRoom: { type: "value" },
  casualDining: { type: "value" },
  mediaRooms: { type: "value" },
  loftGameFlexRooms: { type: "value" },
  offices: { type: "value" },
  workspaces: { type: "value" },
  garageAttached: { type: "value" },
  garageDetached: { type: "value" },
  garagePort: { type: "value" },
  primaryBedroom: { type: "value" },
  garageConfiguration: { type: "value" },
  basementConfig: { type: "value" },
  fieldProposals: { type: "list", update: "incremental", config: aiFieldProposalConfig },
};

function mapToForm(
  planPackage: PlanPackageProgramDataV2_PlanPackageFragment,
  includeProposals: boolean,
): ProgramDataForm {
  const { id, __typename, readyPlanProgramData, primaryBedroom, garageConfiguration, basementConfig, ...numerics } =
    planPackage.programData;
  return {
    ...numerics,
    primaryBedroom: primaryBedroom?.code,
    garageConfiguration: garageConfiguration?.code,
    basementConfig: basementConfig?.code,
    fieldProposals: includeProposals ? flattenEntityProposals(planPackage.aiProposal?.entityProposals) : [],
  };
}
